// ===============================================================================
// src/lib/pageTitle.js - opt-in override for the phone top bar title.
//
// By default the phone top bar (Layout.jsx) shows the route's title, derived
// from the sidebar nav labels (lib/pageTitleCore.js). A view that has a more
// specific name for what is on screen can say so:
//
//   import { usePhoneTitle } from "../lib/pageTitle";
//   usePhoneTitle(cm && cm.name);   // falsy = no override, route title shows
//
// The override lasts while the calling component is mounted (and the value is
// truthy); it clears itself on unmount, so navigating away never leaves a stale
// title behind. If two mounted components both set one, the most recent wins
// and the other comes back when it unmounts.
//
// The override is NOT copied into document.title. A view may pass a patient's
// name here, and the tab title is visible on shared screens and is written to
// browser history - so document.title always carries only the route title.
//
// A tiny module store (useSyncExternalStore) rather than a context provider:
// the setter is used deep inside routed views and the reader is the shell, and
// a store needs no provider wired around <Outlet/>.
// ===============================================================================

import { useEffect, useSyncExternalStore } from "react";

let stack = [];               // [{ title }] in mount order; last one shows
const listeners = new Set();

function emit() { listeners.forEach((fn) => fn()); }
function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function getSnapshot() { return stack.length ? stack[stack.length - 1].title : null; }

/** Set a more specific phone top bar title while the caller is mounted. */
export function usePhoneTitle(title) {
  const value = title == null ? "" : String(title).trim();
  useEffect(() => {
    if (!value) return undefined;
    const entry = { title: value };
    stack = stack.concat(entry);
    emit();
    return () => {
      stack = stack.filter((e) => e !== entry);
      emit();
    };
  }, [value]);
}

/** The active override, or null. Read by Layout's phone top bar only. */
export function usePhoneTitleOverride() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
